// src/pages/AwardResultsPage.js
import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
} from "@mui/material";
import { useData } from "context/DataContext";

const byScoreDesc = (a, b) => {
  const scoreA = Number(a.data.rollup_average_scores ?? -Infinity);
  const scoreB = Number(b.data.rollup_average_scores ?? -Infinity);
  // Put “NaN” or empty scores at the end
  if (isNaN(scoreA) && isNaN(scoreB)) return 0;
  if (isNaN(scoreA)) return 1;
  if (isNaN(scoreB)) return -1;
  return scoreB - scoreA;
};

const AwardResultsPage = () => {
  const { awards, projects, fairEvent } = useData();

  /* Build a quick project lookup */
  const projectMap = useMemo(
    () =>
      projects.reduce((acc, rec) => {
        acc[rec.id] = rec; // store whole record for later
        return acc;
      }, {}),
    [projects]
  );

  /* ------------- Award recipients (one list item per project) ----- */
  const juniorAwardees = useMemo(
    () =>
      awards
        .filter((aw) => aw.data.award_division?.startsWith("Junior"))
        .map((aw) => ({
          awardName: aw.data.Name,
          projects: aw.data.fk_project
            .map((pid) => projectMap[pid])
            .filter(Boolean),
        })),
    [awards, projectMap]
  );

  const seniorAwardees = useMemo(
    () =>
      awards
        .filter((aw) => aw.data.award_division?.startsWith("Senior"))
        .map((aw) => ({
          awardName: aw.data.Name,
          projects: aw.data.fk_project
            .map((pid) => projectMap[pid])
            .filter(Boolean),
        })),
    [awards, projectMap]
  );

  /* ------------- Full-division tables ---------------------------- */
  const juniorProjects = useMemo(
    () =>
      projects
        .filter((p) => {
          console.log(fairEvent);
          console.log(p.data.fair_year);
          return (
            p.data.project_division?.startsWith("Junior") &&
            Array.isArray(p.data.fair_year) &&
            p.data.fair_year.includes(fairEvent?.record_id) &&
            p.data.rollup_average_scores > 0 // must have valid score
          );
        })
        .sort(byScoreDesc),
    [projects, fairEvent]
  );

  const seniorProjects = useMemo(
    () =>
      projects
        .filter(
          (p) =>
            p.data.project_division?.startsWith("Senior") &&
            Array.isArray(p.data.fair_year) &&
            p.data.fair_year.includes(fairEvent?.record_id) &&
            p.data.rollup_average_scores > 0 // must have valid score
          // p.data.fair_year.includes(fairEvent?.id)
        )
        .sort(byScoreDesc),
    [projects, fairEvent]
  );

  /* ------------- Render ------------------------------------------ */
  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 4 }}>
      {/* Title */}
      <Typography variant="h3" sx={{ fontWeight: 700, textAlign: "center" }}>
        {fairEvent?.nyssc_title ||
          "2025 STANYS New York State Science Congress"}
      </Typography>

      {/* ========================================================================= */}
      {/* Junior Division Award Recipients                                          */}
      {/* ========================================================================= */}
      <SectionHeader text="Junior Division Award Recipients" />
      {juniorAwardees.map((aw) => (
        <AwardBlock key={aw.awardName} {...aw} />
      ))}

      {/* ========================================================================= */}
      {/* Senior Division Award Recipients                                          */}
      {/* ========================================================================= */}
      <SectionHeader text="Senior Division Award Recipients" />
      {seniorAwardees.map((aw) => (
        <AwardBlock key={aw.awardName} {...aw} />
      ))}

      {/* ========================================================================= */}
      {/* Full Junior Division Table                                                */}
      {/* ========================================================================= */}
      <SectionHeader text="Full Junior Division – Ranked by Average Score" />
      <ProjectTable rows={juniorProjects} />

      {/* ========================================================================= */}
      {/* Full Senior Division Table                                                */}
      {/* ========================================================================= */}
      <SectionHeader text="Full Senior Division – Ranked by Average Score" />
      <ProjectTable rows={seniorProjects} />
    </Box>
  );
};

/* ------------------------------------------------------------------ */
/*  Small helper components                                            */
/* ------------------------------------------------------------------ */

const SectionHeader = ({ text }) => (
  <>
    <Typography
      variant="h5"
      sx={{ fontWeight: 700, mt: 6, mb: 1, textTransform: "uppercase" }}
    >
      {text}
    </Typography>
    <Divider sx={{ mb: 2 }} />
  </>
);

const AwardBlock = ({ awardName, projects }) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="h6" sx={{ fontWeight: 700 }}>
      {awardName}
    </Typography>

    <List dense>
      {projects.map((proj) => (
        <ListItem key={proj.id} disablePadding>
          <ListItemText
            primary={proj.data.project_title}
            secondary={proj.data.rollup_students}
            primaryTypographyProps={{ fontWeight: 600 }}
          />
        </ListItem>
      ))}
    </List>
  </Box>
);

/* Simple table of projects */
const ProjectTable = ({ rows }) => (
  <Paper elevation={1} sx={{ overflowX: "auto", mb: 4 }}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 700 }}>Rank</TableCell>
          <TableCell sx={{ fontWeight: 700 }}>Project Title</TableCell>
          <TableCell sx={{ fontWeight: 700 }}>Students</TableCell>
          {/* <TableCell sx={{ fontWeight: 700 }} align="right">
            Avg Score
          </TableCell> */}
        </TableRow>
      </TableHead>

      <TableBody>
        {rows.map((proj, i) => (
          <TableRow key={proj.id}>
            <TableCell>{i + 1}</TableCell>
            <TableCell>{proj.data.project_title}</TableCell>
            <TableCell>{proj.data.rollup_students}</TableCell>
            {/* <TableCell align="right">
              {Number(proj.data.rollup_average_scores).toFixed(2) || "—"}
            </TableCell> */}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Paper>
);

export default AwardResultsPage;
